import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  Paper,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { Box } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import { DataAccuracyEndPointLogic } from "../../../../../logic/AssetManagement/Health/Data Accuracy/dataAccuracyEndPointLogic";
import {
  selectIsLoading,
  selectUserData,
  // selectUserDetails,
  selectUserToken,
  selectUserWebsitePermissions,
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../../../features/userSlice";
import store from "../../../../../store";
import SimpleBackdrop from "../../../../BackDrop/BackDrop";
import {
  setIsSuccess,
  setSelectedRowsData,
} from "../../../../../features/dataAccuracySlice";
import { getSiteInfo } from "../../../../../utils/helpers";
import DataStatusAlert from "../UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CachedIcon from "@mui/icons-material/Cached";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SnoozeIcon from "@mui/icons-material/Snooze";
import {
  fetchDataAccuracy,
  getMissingQueryOutputFields,
} from "../DataAccuracyHelpers/helpers";
import { useSearchParams } from "react-router-dom";

const SettingsDataAccuracy = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [comment, setComment] = useState<string>("");
  const [searchParams] = useSearchParams();
  const {
    isSuccess,
    selectedRowsData = [],
    allDimensions,
  } = useSelector((state: any) => state.dataAccuracy);
  // Collect all dynamic columns across selected rows
  const dynamicColumns = Array.from(
    new Set(
      selectedRowsData.flatMap((row: any) => getMissingQueryOutputFields(row)),
    ),
  );

  const allWebsites = useSelector(selectUserWebsitePermissions);
  const [selected, setSelected] = useState<any[]>([]);
  // Track individual row changes for status and snooze dates
  const [rowChanges, setRowChanges] = useState<{
    [key: string]: { status?: string; snoozeDate?: string | null };
  }>({});

  const token = useSelector(selectUserToken);
  const loading = useSelector(selectIsLoading);
  const dispatch = useDispatch();
  const queryId = "5637377896742912";
  const fetchBy = searchParams.get("fetchBy") || "Website";
  const type = fetchBy === "Website" ? "site" : "dimension";
  const selectedSites = searchParams.get("selected")?.split(",");
  const siteAndDimensionsList = useSelector(selectUserData);
  const dimensionList = siteAndDimensionsList?.data?.dimensions;
  const siteList = siteAndDimensionsList?.data?.website_permissions;
  const assetSharedUid = searchParams.get("asset_shared_uids")?.split(",");
  const taskStructureSharedUid = searchParams.get("task_structure_shared_uid");
  const allDimenssionList = allDimensions.filter(
    (dimension: any) =>
      dimension.organization_id__organization_name ===
      userPermittedOrganizationName,
  );
  const filteredDimensionList = allDimenssionList.map((dimension: any) => {
    return dimension.dimension;
  });
  const userPermittedOrganizationName =
    siteAndDimensionsList?.data?.user_details?.organization_name;

  // Keep local `selected` in sync whenever dialog opens or selectedRowsData changes
  useEffect(() => {
    if (open) {
      setSelected(selectedRowsData || []);
      setRowChanges({});
    }
  }, [selectedRowsData, open]);

  // const userEmail = useSelector(selectUserDetails)?.email || "";

  const snoozeBody = {
    tasks: selected.map((task) => {
      const rowKey = task?.shared_uid;
      const changes = rowChanges[rowKey] || {};

      const taskObj: any = {
        current_status: changes.status || task.current_status,
        shared_uid: task?.shared_uid,
        // modified_by: userEmail,
        // modified_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };

      // If status is Snoozed, include snooze_end date
      if (taskObj.current_status === "Snooze") {
        taskObj.snooze_end = changes.snoozeDate;
      }

      if (comment?.trim()) {
        taskObj.reason = comment.trim();
      }

      return taskObj;
    }),
  };

  const isAllSelected =
    selected.length > 0 && selected.length === selectedRowsData.length;

  const handleSelectRow = (row: any) => {
    setSelected((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row],
    );
  };

  const handleSelectAll = () => {
    setSelected(isAllSelected ? [] : [...selectedRowsData]);
  };

  const handleStatusChange = (row: any, newStatus: string) => {
    const rowKey = row?.shared_uid;
    setRowChanges((prev) => ({
      ...prev,
      [rowKey]: {
        ...prev[rowKey],
        status: newStatus,
      },
    }));
  };

  const handleRowSnoozeDateChange = (row: any, date: any) => {
    const rowKey = row?.shared_uid;
    setRowChanges((prev) => ({
      ...prev,
      [rowKey]: {
        ...prev[rowKey],
        snoozeDate: date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : null,
      },
    }));
  };

  const clearLocalState = () => {
    setSelected([]);
    setComment("");
    setRowChanges({});
  };

  const callAPIs = async () => {
    try {
      if (selected.length === 0) {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Please select at least one task.",
            type: "error",
          }),
        );
        return;
      }

      // Validate that all snoozed tasks have a date
      const hasInvalidSnooze = selected.some((task) => {
        const rowKey = task?.shared_uid;
        const changes = rowChanges[rowKey] || {};
        const status = changes.status || task.current_status;

        return status === "Snooze" && !changes.snoozeDate;
      });

      if (hasInvalidSnooze) {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Please provide a snooze date for all snoozed tasks.",
            type: "error",
          }),
        );
        return;
      }

      store.dispatch(setIsLoading(true));

      const response = await DataAccuracyEndPointLogic.updateDataAccuracyTask(
        token,
        snoozeBody,
        dispatch,
      );
      // Proceed ONLY if status is 200
      if (response?.status === 200 || response?.status === 207) {
        clearLocalState();
        dispatch(setSelectedRowsData([]));
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Tasks updated successfully",
            type: "success",
          }),
        );
        await fetchDataAccuracy({
          token,
          queryId,
          type,
          fetchBy,
          selectedSites,
          filteredDimensionList,
          dimensionList,
          siteList,
          taskStructureSharedUid,
          assetSharedUid,
          dispatch,
        });
        onClose();
      } else {
        // API responded but not successful
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Update failed. Please try again.",
            type: "error",
          }),
        );
      }
    } catch (error) {
      console.error("Update API error:", error);
      store.dispatch(
        setDisplayErrorSnackBar({
          message: "Update failed. Please try again.",
          type: "error",
        }),
      );
    } finally {
      store.dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    if (isSuccess) {
      store.dispatch(setIsSuccess(false));
    }
    // eslint-disable-next-line
  }, [isSuccess]);

  // Helper to get current status for a row
  const getCurrentStatus = (row: any) => {
    const rowKey = row?.shared_uid;
    const changes = rowChanges[rowKey];
    return changes?.status || row.current_status;
  };

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="lg" fullWidth>
      {loading && <SimpleBackdrop />}
      <DialogTitle>Change Tasks Status</DialogTitle>
      <DialogContent>
        <Typography gutterBottom sx={{ paddingBottom: "8px" }}>
          Selected Tasks
        </Typography>
        <Paper sx={{ width: "100%", overflowY: "auto", maxHeight: "400px" }}>
          <Table size="small" aria-label="a dense table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>

                {/* FIXED COLUMNS */}
                <TableCell sx={{ padding: "4px", minWidth: "150px" }}>
                  Task Name
                </TableCell>
                <TableCell sx={{ padding: "4px", minWidth: "120px" }}>
                  Asset
                </TableCell>
                {/* DYNAMIC QUERY_OUTPUT COLUMNS */}
                {dynamicColumns.map((col: any) => (
                  <TableCell
                    key={col}
                    sx={{ padding: "4px", minWidth: "200px" }}
                  >
                    {selectedRowsData[0]?.data?.[col]?.title || col}
                  </TableCell>
                ))}
                <TableCell sx={{ padding: "4px", minWidth: "150px" }}>
                  Status
                </TableCell>
                <TableCell sx={{ padding: "4px", minWidth: "200px" }}>
                  Snoozed Until
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {selectedRowsData.map((row: any, index: number) => {
                const currentStatus = getCurrentStatus(row);
                const isSnoozed = currentStatus === "Snooze";

                return (
                  <TableRow
                    key={index}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell padding="checkbox" sx={{ padding: "4px" }}>
                      <Checkbox
                        size="small"
                        color="secondary"
                        checked={selected.some((r) => r === row)}
                        onChange={() => handleSelectRow(row)}
                      />
                    </TableCell>
                    <TableCell sx={{ padding: "4px" }}>
                      {row?.task_structure__title}
                    </TableCell>

                    <TableCell sx={{ padding: "4px" }}>
                      {getSiteInfo(allWebsites, row?.website_shared_uid)}
                    </TableCell>

                    {/* DYNAMIC COLUMNS CONTENT */}
                    {dynamicColumns.map((col: any) => (
                      <TableCell key={col} sx={{ padding: "4px" }}>
                        {row?.data?.[col]?.value ?? "-"}
                      </TableCell>
                    ))}

                    <TableCell sx={{ padding: "4px" }}>
                      <Select
                        value={currentStatus}
                        onChange={(e) =>
                          handleStatusChange(row, e.target.value)
                        }
                        size="small"
                        color="secondary"
                        variant="standard"
                        disableUnderline
                        sx={{
                          fontSize: "13px",
                          "& .MuiSelect-select": {
                            padding: "0",
                            paddingRight: "20px !important",
                            display: "inline-flex",
                            alignItems: "center",
                          },
                        }}
                        IconComponent={(props) => (
                          <span
                            {...props}
                            style={{ fontSize: "12px", marginLeft: "4px" }}
                          >
                            ▼
                          </span>
                        )}
                        renderValue={(value) => (
                          <DataStatusAlert status={value} />
                        )}
                      >
                        <MenuItem value="To Do">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <ErrorOutlineIcon
                              style={{ color: "#5F2120", fontSize: 17 }}
                            />
                            To Do
                          </Box>
                        </MenuItem>
                        <MenuItem value="In Progress">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <CachedIcon
                              style={{ color: "#7B1FA2", fontSize: 17 }}
                            />
                            In Progress
                          </Box>
                        </MenuItem>
                        <MenuItem value="Snooze">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <SnoozeIcon
                              style={{ color: "#5a5a5a", fontSize: 17 }}
                            />
                            Snooze
                          </Box>
                        </MenuItem>
                      </Select>
                    </TableCell>

                    <TableCell sx={{ padding: "4px" }}>
                      {isSnoozed ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              slotProps={{
                                textField: {
                                  size: "small",
                                  color: "secondary",
                                  onKeyDown: (e: any) => e.preventDefault(),
                                  sx: {
                                    width: "160px",
                                    "& .MuiInputBase-input": {
                                      fontSize: "13px",
                                    },
                                  },
                                  placeholder: "DD/MM/YYYY",
                                },
                              }}
                              format="DD/MM/YYYY"
                              minDate={dayjs().startOf("day")}
                              value={
                                rowChanges[row?.shared_uid]?.snoozeDate
                                  ? dayjs(
                                      rowChanges[row?.shared_uid]?.snoozeDate,
                                    )
                                  : row.snooze_end
                                    ? dayjs(row.snooze_end)
                                    : null
                              }
                              onChange={(date: any) =>
                                handleRowSnoozeDateChange(row, date)
                              }
                            />
                          </LocalizationProvider>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
        {/* Comment Section */}

        <Box sx={{ paddingTop: 2 }}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              placeholder="Please Write Reason Here"
              label="Reason"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              color="secondary"
              fullWidth
              multiline
              rows={5}
              disabled={selected.length === 0}
              helperText={
                selected.length === 0
                  ? "Please select a row to add comments."
                  : ""
              }
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ marginBottom: "16px", paddingRight: "40px" }}>
        <Button
          onClick={() => {
            if (!loading) {
              clearLocalState();
              onClose();
            }
          }}
          color="secondary"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          sx={{ whiteSpace: "nowrap" }}
          disabled={loading || selected.length === 0}
          onClick={callAPIs}
          data-tracker="604"
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <>
              Save
              <SaveOutlinedIcon
                sx={{ color: "#fff", marginLeft: "5px" }}
                fontSize="small"
              />
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDataAccuracy;
